import { queryOptions, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/** InformeEstadoConteo es el nº de LOTOs en un estado del enum (siempre los cuatro estados). */
export type InformeEstadoConteo = {
  estado: string;
  total: number;
};

/**
 * InformeMesConteo son las aperturas de un mes ("2026-07", UTC) y cuántas acabaron cerradas.
 */
export type InformeMesConteo = {
  mes: string;
  total: number;
  cerrados: number;
};

/** InformeEquipoConteo es el nº de LOTOs de un equipo (ranking descendente, top 10). */
export type InformeEquipoConteo = {
  id_equipo: number;
  equipo: string;
  codigo_equipo: string;
  total: number;
};

/**
 * InformeLotos son los agregados estadísticos de los LOTOs de una obra
 * (GET /api/v1/obras/{obraId}/informes/lotos, #215): total, desglose por estado, aperturas por
 * mes, duración media de los cerrados (null si no hay), ranking por equipo y candados activos.
 */
export type InformeLotos = {
  total: number;
  por_estado: InformeEstadoConteo[];
  por_mes: InformeMesConteo[];
  duracion_media_horas: number | null;
  por_equipo: InformeEquipoConteo[];
  candados_activos: number;
};

/** RangoFechas es el filtro opcional [desde, hasta] (YYYY-MM-DD) sobre fecha_apertura. */
export type RangoFechas = {
  desde?: string;
  hasta?: string;
};

// construyeQuery arma el query-string de las fechas presentes (omite las vacías).
function construyeQuery(rango: RangoFechas): string {
  const params = new URLSearchParams();
  if (rango.desde) {
    params.set("desde", rango.desde);
  }
  if (rango.hasta) {
    params.set("hasta", rango.hasta);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * informeLotosQueryOptions lee el informe de la obra `obraId` con el rango dado. La caché se
 * namespacea por obra Y por rango (`["obra", obraId, "informes", "lotos", rango]`), de modo que
 * cambiar el filtro reconsulta sin mezclar rangos ni obras. Es una lectura solo-online (#215):
 * no entra en el outbox offline; sin red, la query cae en su estado de error estándar.
 */
export function informeLotosQueryOptions(obraId: number, rango: RangoFechas) {
  return queryOptions({
    queryKey: ["obra", obraId, "informes", "lotos", rango.desde ?? "", rango.hasta ?? ""] as const,
    queryFn: () => api.get<InformeLotos>(`/obras/${obraId}/informes/lotos${construyeQuery(rango)}`),
  });
}

export function useInformeLotos(obraId: number, rango: RangoFechas) {
  return useQuery(informeLotosQueryOptions(obraId, rango));
}
